import { formatRefDate } from '../../utils/formatRefDate';
import { useEffect, useRef, useState } from 'react'
import Maps from './Maps';
import { GetCurrent, GetEmail, GetMonitoredObject, GetSetup } from '../../Api/getData';
import TableMonitoring from './TableMonitoring';
import { HiChartBar, HiClock, HiOfficeBuilding, HiMap, HiRefresh, HiStatusOnline, HiStatusOffline } from 'react-icons/hi';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { Alert, Button, message, Skeleton } from 'antd';
import { monitoringSummary, positionReports } from './monitoringData';
import PositionReports from './PositionReports';
import './monitoring.css';

const Monitoring = () => {

    useEffect(() => {
        document.title = "NextSense - Monitoring";
    }, []);

    const [dataShip, setDataShip] = useState([]);
    const [dataJoined, setDataJoined] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const mapPanel = useRef(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [loadError, setLoadError] = useState('');
    useEffect(() => {
        const onChange = () => setFullscreen(document.fullscreenElement === mapPanel.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement === mapPanel.current) await document.exitFullscreen();
            else if (mapPanel.current?.requestFullscreen) await mapPanel.current.requestFullscreen();
            else message.info('Fullscreen is not supported by this browser.');
        } catch { message.info('Fullscreen could not be opened.'); }
    };
    const [loadingRows, setLoadingRows] = useState({});


    const fetchAndJoinData = async () => {
        try {
            setIsLoading(true);
            setLoadError('');
            setDataShip([]);
            setDataJoined([]);
            setLoadingRows({});

            // Fetch ship list
            let shipList = [];
            try {
                const shipRes = await GetMonitoredObject();
                shipList = shipRes?.Ship || [];
            } catch (error) {
                shipList = [];
                setLoadError('Unable to load vessels. Please try Refresh.');
                setIsLoading(false);
                return;
            }

            const setupList = await GetSetup();
            const filteredSetupList = setupList.filter(item => item.AccountId !== 'SINERGI');

            // Fetch email data
            const emailRes = await Promise.all(
                filteredSetupList.map(async (item) => {
                    try {
                        const emailData = await GetEmail({ ObjectCode: item.ObjectCode });

                        return {
                            AccountId: item.AccountId,
                            ObjectCode: item.ObjectCode,
                            email: emailData
                        };
                    } catch (error) {
                        return {
                            AccountId: item.AccountId,
                            ObjectCode: item.ObjectCode,
                            email: []
                        };
                    }
                })
            );

            // console.log('emailRes:', emailRes);

            // try {
            //     emailRes = await GetAllEmail();
            // } catch (error) {
            //     emailRes = [];
            // }

            // Set initial loading state untuk semua ship
            const initialLoadingState = {};
            shipList.forEach(ship => {
                initialLoadingState[ship.ShipCode] = true;
            });
            setLoadingRows(initialLoadingState);
            setIsLoading(false);

            // Fetch current data for each ship progressively
            const tempDataShip = [];
            const tempDataJoined = [];

            for (let i = 0; i < shipList.length; i++) {
                const ship = shipList[i];

                try {
                    const currentData = await GetCurrent({
                        AccountId: ship.KodeCabang,
                        ObjectCode: ship.ShipCode
                    });

                    // Only add if current data is valid
                    if (currentData && currentData.length > 0) {
                        const matchingEmail = emailRes.find(
                            email => email.ObjectCode === ship.ShipCode
                        );

                        const shipWithCurrent = {
                            ...ship,
                            current: currentData
                        };

                        const joinedItem = {
                            ...ship,
                            email: matchingEmail?.email || [],
                            current: currentData
                        };

                        // console.log('matchingEmail:', matchingEmail);

                        tempDataShip.push(shipWithCurrent);
                        tempDataJoined.push(joinedItem);

                        // Update state progressively - tampilkan data yang sudah berhasil
                        setDataShip([...tempDataShip]);
                        setDataJoined([...tempDataJoined]);

                    }

                    // Remove loading state untuk ship ini
                    setLoadingRows(prev => {
                        const newState = { ...prev };
                        delete newState[ship.ShipCode];
                        return newState;
                    });
                } catch (error) {
                    // Remove loading state untuk ship yang error
                    setLoadingRows(prev => {
                        const newState = { ...prev };
                        delete newState[ship.ShipCode];
                        return newState;
                    });
                    continue;
                }
            }

        } catch (error) {
            console.log('Error fetching data:', error)
            setLoadError('Unable to complete monitoring data. Please try Refresh.');
        } finally {
            setIsLoading(false);
            setLoadingRows({});
        }
    };

    // console.log("Data dataJoined:", dataJoined);


    useEffect(() => {

        let intervalId;

        fetchAndJoinData(); // Call immediately

        intervalId = setInterval(fetchAndJoinData, 1800000); // Every 30 minutes

        return () => clearInterval(intervalId); // Cleanup on unmount
    }, []);


    const handleRefresh = () => {
        fetchAndJoinData();
    }

    const reports = positionReports(dataJoined);
    const summary = monitoringSummary(dataShip, reports);
    const busy = isLoading || Object.keys(loadingRows).length > 0;
    const metrics = [
        { label: 'Vessels with Data', value: summary.total, caption: 'Loaded sensor readings', icon: HiOfficeBuilding },
        { label: 'Online', value: summary.online, caption: 'Existing API sensor status', icon: HiStatusOnline, tone: 'green' },
        { label: 'Offline', value: summary.offline, caption: 'Existing API sensor status', icon: HiStatusOffline, tone: 'muted' },
        { label: 'Locations Available', value: summary.locations, caption: 'Valid position reports', icon: HiMap },
        { label: 'Latest Sensor Reading', value: formatRefDate(summary.latest), caption: 'Latest RefDate across sensors', icon: HiClock, date: true },
    ];
    return (
        <main className="monitoring-page">
            <header className="monitoring-header">
                <div className="monitoring-heading"><span className="monitoring-page-icon"><HiChartBar /></span>
                    <div><h1>Vessel Monitoring</h1><p>Vessel tracking and sensor data</p></div>
                </div>
                <Button type="primary" icon={<HiRefresh />} loading={busy} onClick={handleRefresh}>Refresh</Button>
            </header>
            {loadError && <Alert type="error" showIcon message={loadError} />}
            <div className="monitoring-summary">
                {metrics.map(({ label, value, caption, icon: Icon, tone = '', date }) => <div className={`monitoring-metric ${tone}`} key={label}>
                    <span className="monitoring-icon"><Icon /></span><div><h2>{label}</h2>
                    {isLoading ? <Skeleton.Input active={false} size="small" /> : <p className={date ? 'monitoring-date-value' : 'monitoring-value'}>{value}</p>}
                    <small>{caption}</small></div>
                </div>)}
            </div>
            <div className="monitoring-main-grid">
                <section className="monitoring-panel" aria-label="Vessel List">
                    <header className="monitoring-panel-heading"><span className="monitoring-icon"><HiOfficeBuilding /></span>
                        <div><h2>Vessel List</h2><p>All vessels with loaded sensor data</p></div></header>
                    <TableMonitoring dataSource={dataShip} isLoading={isLoading} loadingRows={loadingRows} updateData={handleRefresh} />
                    <p className="monitoring-note">Online/Offline follows the existing API sensor status; it does not verify vessel connectivity.</p>
                </section>
                <section ref={mapPanel} className="monitoring-panel monitoring-map-panel" aria-label="Vessel Locations">
                    <header className="monitoring-panel-heading"><span className="monitoring-icon green"><HiMap /></span>
                        <div><h2>Vessel Locations</h2><p>Latest available vessel position reports</p></div>
                        <Button size="small" icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen}>
                            {fullscreen ? 'Exit Fullscreen' : 'View in Fullscreen'}
                        </Button>
                    </header>
                    <div className="monitoring-map"><Maps dataSource={dataJoined} /></div>
                    {!reports.length && <p className="monitoring-note">{busy ? 'Loading position reports...' : 'No valid vessel locations available.'}</p>}
                </section>
            </div>
            <section className="monitoring-panel" aria-label="Latest Position Reports">
                <header className="monitoring-panel-heading"><span className="monitoring-icon"><HiClock /></span>
                    <div><h2>Latest Position Reports</h2><p>One latest available report per vessel, not event history</p></div></header>
                <PositionReports reports={reports} loading={isLoading} />
            </section>
        </main>
    );
};
export default Monitoring;
